/* =============================================================
   CYBHI Participation Checklist — structured content (v2).

   Source: "CYBHI Participation Checklist for LEAs —
            Onboarding • Optional • Operational" PDF
   which in turn cites:
     - DHCS CYBHI Fee Schedule Program Manual (August 2025)
     - CYBHI Fee Schedule Program Participation Models (December 2025)
     - CYBHI Fee Schedule Program Participation of Community Providers (April 2025)
     - CYBHI Fee Schedule Program ORP Practitioners (September 2025)

   Top-level structure mirrors the source PDF's three-tab grouping:
     ONBOARDING  (sections 1–4)  — mandatory to participate
     OPTIONAL    (section 5)     — recommended best practices
     OPERATIONAL (section 6)     — ongoing post-go-live cadence

   Item IDs encode the source section path (e.g. "1.5.2" = §1.5 item 2).
   Applicability gates (pathways / sub-pathways / scenarios) still
   filter within a category — selecting Pathway C1, for example, hides
   the Type II NPI and PAVE items inside the Onboarding tab.
   ============================================================ */

import type {
  CategoryMeta,
  ChecklistCategory,
  PathwayMeta,
  ScenarioMeta,
  SubPathwayCMeta,
} from './types';

// ============================================================
// Top-tab metadata — drives the 3-tab UI.
// ============================================================

export const CATEGORIES: CategoryMeta[] = [
  {
    id: 'onboarding',
    label: 'Onboarding',
    tagline: 'Mandatory to participate',
    description:
      'Items required by DHCS, Carelon Behavioral Health, or applicable law before the LEA can begin participating in CYBHI. Work these first.',
    accent: '#c97a32',
    accentSoft: '#f4ddc1',
  },
  {
    id: 'optional',
    label: 'Optional',
    tagline: 'Recommended best practices',
    description:
      'Not required by DHCS or CBH, but materially improve security posture, denial rates, audit defensibility, and operational performance. Each item lists the benefit it delivers.',
    accent: '#5d7a3a',
    accentSoft: '#dfe6cd',
  },
  {
    id: 'operational',
    label: 'Operational',
    tagline: 'Ongoing post-onboarding',
    description:
      'Recurring activities the LEA must perform after go-live to maintain compliance and operational performance — daily, weekly, monthly, quarterly, annually, every five years, and event-driven.',
    accent: '#3e6e8c',
    accentSoft: '#cfdee8',
  },
];

// ============================================================
// Selector metadata — pathway / scenario picker.
// ============================================================

export const PATHWAYS: PathwayMeta[] = [
  {
    id: 'A',
    name: 'Independent LEA / IHE',
    short: 'Independent',
    description:
      'The LEA submits claims and receives reimbursement directly. Best for medium-large LEAs with billing experience.',
  },
  {
    id: 'B',
    name: 'Consortium Lead LEA / IHE',
    short: 'Consortium Lead',
    description:
      'The LEA serves as intermediary for participating member LEAs/IHEs. Common for County Offices of Education leading regional districts.',
  },
  {
    id: 'C',
    name: 'Consortium Participating Member',
    short: 'Consortium Member',
    description:
      'The LEA joins under a Lead LEA/IHE that handles most administrative functions. Best for LEAs with limited billing infrastructure.',
  },
];

export const SUB_PATHWAYS_C: SubPathwayCMeta[] = [
  {
    id: 'C1',
    name: 'Lead handles both claims and reimbursement',
    description:
      'Lightest path. Member needs NO Medi-Cal enrollment, NO Type II NPI, NO Availity account.',
  },
  {
    id: 'C2',
    name: 'Member handles claims and/or reimbursement directly',
    description:
      'Member needs Type II NPI, Medi-Cal enrollment, and an Availity account — but joins under a Lead administratively.',
  },
  {
    id: 'C3',
    name: 'Member submits data files directly to Carelon',
    description:
      'Member submits its own SPI roster and student batch files but Lead claims and receives funds. Member needs its own sFTP connection; tri-party DUA required.',
  },
];

export const SCENARIOS: ScenarioMeta[] = [
  {
    id: 'S1',
    name: 'Direct LEA employees only',
    short: 'Direct employees',
    description:
      'PPS-credentialed practitioners, school nurses, social workers, MFTs, LPCCs, psychologists employed by the LEA.',
  },
  {
    id: 'S2',
    name: 'Embedded community providers (LEA contracts and bills)',
    short: 'Embedded community',
    description:
      'LEA contracts with a community-based organization, clinic, or individual practitioner; LEA bills, then pays the contractor under contract terms.',
  },
  {
    id: 'S3',
    name: 'Affiliated community providers (no contract)',
    short: 'Affiliated (no contract)',
    description:
      'LEA refers students to a community provider with no contractual or financial relationship. The community provider bills Carelon directly.',
  },
  {
    id: 'S4',
    name: 'Statewide affiliated providers',
    short: 'Statewide affiliated',
    description:
      'Multi-county, multi-site community providers serving students from many LEAs. Participate without requiring the LEA to be in an active CYBHI cohort.',
  },
  {
    id: 'S5',
    name: 'Third-party billing vendor',
    short: '3rd-party billing vendor',
    description:
      'The LEA uses an external vendor to handle claims submission. The vendor needs additional agreements with Carelon (tri-party DUA).',
  },
  {
    id: 'S6',
    name: 'Shared EHR / Student Information System (SIS) vendor',
    short: 'Shared EHR / SIS',
    description:
      'The LEA uses a shared documentation or SIS system — often via a County Office of Education or a SaaS vendor — that needs data-sharing accommodations.',
  },
];

// ============================================================
// The checklist itself.
// ============================================================

export const CHECKLIST: ChecklistCategory[] = [
  // ==========================================================
  // ONBOARDING — PDF Part 1 (Sections 1–4)
  // ==========================================================

  // ----------------------------------------------------------
  // §1 — Universal Onboarding (required for every LEA)
  // ----------------------------------------------------------
  {
    id: 'on-1',
    number: '1',
    title: 'Universal Onboarding',
    description:
      'Required for every LEA regardless of pathway or scenario. Work these in order before the scenario-specific sections below.',
    category: 'onboarding',
    sections: [
      {
        id: '1.1',
        number: '1.1',
        title: 'Choose Participation Pathway',
        items: [
          { id: '1.1.1', text: 'Confirm which of the three pathways applies (Independent LEA / IHE, Consortium Lead, or Consortium Member).' },
          { id: '1.1.2', text: 'Identify and confirm the Lead LEA/IHE you will participate under.', appliesTo: { pathways: ['C'] } },
          { id: '1.1.3', text: 'Identify your participating members and obtain attestation letters from each.', appliesTo: { pathways: ['B'] } },
        ],
      },
      {
        id: '1.2',
        number: '1.2',
        title: 'Organizational Decisions and Team',
        items: [
          { id: '1.2.1', text: 'Identify the executive sponsor (typically Superintendent, Assistant Superintendent of Pupil Services, or CFO).' },
          { id: '1.2.2', text: 'Form a CYBHI implementation team with representation from pupil services, business / finance, IT, HR (for credentialing), legal counsel, and school nurse / health services.' },
          { id: '1.2.3', text: 'Identify the Authorized Representative — the officer with legal authority to sign the PPA, DUA, and binding contracts.' },
          { id: '1.2.4', text: 'If required by LEA policy, obtain governing board approval to participate in CYBHI.' },
        ],
      },
      {
        id: '1.3',
        number: '1.3',
        title: 'Read the Mandatory Source Materials',
        items: [
          { id: '1.3.1', text: 'Read the CYBHI Fee Schedule Program Manual (DHCS, August 2025) end-to-end.' },
          { id: '1.3.2', text: 'Read the CYBHI Fee Schedule Program Participation Models (December 2025).' },
          { id: '1.3.3', text: 'Read the CYBHI Fee Schedule Scope of Services, Codes and Reimbursement Rates.' },
          { id: '1.3.4', text: 'Read the Ordering, Referring, and Prescribing Practitioners guidance (September 2025).' },
          { id: '1.3.5', text: 'Read the Guidance for the Participation of Community Providers (April 2025) — required even if using only directly-employed practitioners.' },
        ],
      },
      {
        id: '1.4',
        number: '1.4',
        title: 'Confirm Statutory Eligibility',
        items: [
          { id: '1.4.1', text: 'Confirm the LEA is a qualifying entity under California Welfare and Institutions Code §5961.4.' },
          { id: '1.4.2', text: 'Confirm the LEA operates a schoolsite per Health and Safety Code §1374.722(b)(6).' },
          { id: '1.4.3', text: 'Confirm the LEA is not on the federal OIG or SAM exclusion lists.' },
        ],
      },
      {
        id: '1.5',
        number: '1.5',
        title: 'Type II National Provider Identifier (NPI) — Organizational',
        description:
          'Required for Independent LEAs, Consortium Leads, and any Consortium Member that will submit claims OR receive reimbursement directly. Not required for Consortium Members where the Lead handles both functions.',
        appliesTo: { pathways: ['A', 'B'], subPathwaysC: ['C2'] },
        items: [
          { id: '1.5.1', text: 'Determine whether the LEA already has a Type II NPI (LEA-BOP participants already do).' },
          { id: '1.5.2', text: 'If not: apply for a Type II NPI through NPPES at nppes.cms.hhs.gov.' },
          { id: '1.5.3', text: 'Required information: legal business name, EIN, business address, primary practice address, authorized official information, taxonomy code (typically 251300000X for LEAs).' },
          { id: '1.5.4', text: "Save the issued NPI in the LEA's central record system." },
        ],
      },
      {
        id: '1.6',
        number: '1.6',
        title: 'Type I NPIs — Individual Practitioners',
        items: [
          { id: '1.6.1', text: 'Confirm every individual practitioner who will render CYBHI services has a Type I NPI (required for licensed and PPS-credentialed practitioners alike).' },
          { id: '1.6.2', text: 'For practitioners without a Type I NPI: practitioner applies individually at NPPES (LEA cannot apply on their behalf).' },
          { id: '1.6.3', text: "Maintain a roster of every practitioner's Type I NPI, license/credential type, license number, and effective dates." },
        ],
      },
      {
        id: '1.7',
        number: '1.7',
        title: 'Medi-Cal Enrollment via PAVE Portal',
        description:
          'Required for Independent LEAs, Consortium Leads, and any Consortium Member that will submit claims or receive direct reimbursement. NOT required for Consortium Members where the Lead handles both functions.',
        appliesTo: { pathways: ['A', 'B'], subPathwaysC: ['C2'] },
        items: [
          { id: '1.7.1', text: 'Visit the DHCS PAVE portal at https://pave.dhcs.ca.gov.' },
          { id: '1.7.2', text: 'Create a PAVE administrator account for the LEA.' },
          { id: '1.7.3', text: 'Complete the appropriate enrollment application as a Local Educational Agency provider type.' },
          { id: '1.7.4', text: 'Upload required documents: articles of formation, W-9 with EIN, Type II NPI confirmation letter, business license if applicable, authorized signer information, disclosure information per 42 CFR §455.104.' },
          { id: '1.7.5', text: 'Pay the application fee if applicable.' },
          { id: '1.7.6', text: 'Allow 60–120 days for DHCS processing. Track status via the PAVE dashboard.' },
          { id: '1.7.7', text: 'Save the issued Medi-Cal provider number in central records.' },
        ],
      },
      {
        id: '1.8',
        number: '1.8',
        title: 'ORP Practitioners — Medi-Cal Enrollment',
        description:
          'Required for every Medi-Cal CYBHI claim. The ORP must be Medi-Cal enrolled (Type I NPI on the claim).',
        appliesTo: { pathways: ['A', 'B'], subPathwaysC: ['C2'] },
        items: [
          { id: '1.8.1', text: "Identify the LEA's pool of potential ORP practitioners — Medi-Cal-enrolled physician or licensed practitioner of the healing arts. Credentialed school nurses may serve as ORP for non-psychotherapy services within scope; cannot serve as ORP for psychotherapy." },
          { id: '1.8.2', text: "Verify each ORP candidate's Medi-Cal enrollment status via the DHCS Provider Master File / PAVE." },
          { id: '1.8.3', text: 'For practitioners not already Medi-Cal enrolled: have them enroll as ORP-only providers via PAVE (streamlined enrollment).' },
          { id: '1.8.4', text: 'Decide the ORP determination model: face-to-face evaluation, standing recommendation (must be reviewed at least annually and based on evidence-based guidelines), or retrospective record review. All three are acceptable to DHCS.' },
          { id: '1.8.5', text: "Document the standing recommendation in each student's record if using that model." },
          { id: '1.8.6', text: 'CONFIRM: ORP NPI requirement applies ONLY to Medi-Cal claims, not commercial Knox-Keene or CDI disability insurance claims.', note: 'Most common Medi-Cal claim rejection cause when missed.' },
        ],
      },
      {
        id: '1.9',
        number: '1.9',
        title: 'Provider Participation Agreement (PPA) with DHCS',
        items: [
          { id: '1.9.1', text: 'Download the appropriate PPA template from the DHCS Resource Library — Standard PPA for Independent LEAs, Consortium Leads, and Members billing directly; Modified PPA for LEA Consortia Members where the Lead handles claims and reimbursement.' },
          { id: '1.9.2', text: 'Review with LEA legal counsel; verify scope, reimbursement terms, audit rights, recoupment, termination terms, data-sharing obligations.' },
          { id: '1.9.3', text: 'Complete all fields: legal name, DBA, EIN, Type II NPI, Medi-Cal provider number, addresses, authorized signer, contact information.' },
          { id: '1.9.4', text: 'Obtain Authorized Representative signature and date; secure board minutes/delegation letter if required.' },
          { id: '1.9.5', text: 'Submit to DHCS at DHCS.SBS@dhcs.ca.gov.' },
          { id: '1.9.6', text: "Save the executed PPA in the LEA's contract repository (minimum 10-year retention)." },
        ],
      },
      {
        id: '1.10',
        number: '1.10',
        title: 'Data Use Agreement (DUA) with Carelon Behavioral Health',
        items: [
          { id: '1.10.1', text: 'Contact CBH at CYBHITPA@carelon.com to request the current DUA template.' },
          { id: '1.10.2', text: 'Determine which DUA variant applies: Two-Party (standard for Independent LEAs and Consortium Leads); Tri-Party (when a billing vendor exchanges data with CBH, or Members submit files directly to CBH); Two-Party + Affiliated Provider Addendum (when designating affiliated community providers via the SPI roster pathway).' },
          { id: '1.10.3', text: 'Review with LEA legal counsel and IT security; confirm alignment with existing HIPAA/FERPA controls.' },
          { id: '1.10.4', text: 'Obtain Authorized Representative signature.' },
          { id: '1.10.5', text: "Submit per CBH's process (typically electronic execution)." },
          { id: '1.10.6', text: 'Save the executed DUA with the PPA.' },
          { id: '1.10.7', text: 'If using a third-party billing vendor: ensure the tri-party DUA is signed BEFORE the vendor handles any CYBHI data.', appliesTo: { scenarios: ['S5'] } },
        ],
      },
      {
        id: '1.11',
        number: '1.11',
        title: 'Establish Secure File Transfer Connection with CBH',
        items: [
          { id: '1.11.1', text: "Set up the LEA's ProviderConnect account with CBH (CBH's HIPAA-compliant secure portal — free, recommended) OR a separate sFTP product." },
          { id: '1.11.2', text: "Complete CBH's ProviderConnect portal training." },
          { id: '1.11.3', text: 'Test file upload and download.' },
        ],
      },
      {
        id: '1.12',
        number: '1.12',
        title: 'Set Up Availity Essentials Account',
        appliesTo: { pathways: ['A', 'B'], subPathwaysC: ['C2'] },
        items: [
          { id: '1.12.1', text: 'Register for an Availity Essentials account at availity.com (CBH-mandated claims interface).' },
          { id: '1.12.2', text: 'Complete Availity onboarding training.' },
          { id: '1.12.3', text: 'Configure claim submission and ERA receipt.' },
        ],
      },
      {
        id: '1.13',
        number: '1.13',
        title: 'Standard Provider Information (SPI) Roster — Initial Submission',
        items: [
          { id: '1.13.1', text: 'Obtain the SPI roster Excel template from CBH or the CYBHI Resource Library.' },
          { id: '1.13.2', text: 'Complete the PROVIDER tab — every practitioner (Type I individuals) and every community provider group (Type G) that will render CYBHI services.' },
          { id: '1.13.3', text: 'For each practitioner, populate: Type I NPI, license/credential type and number, specialty, taxonomy, effective dates, locations served.' },
          { id: '1.13.4', text: 'Submit the SPI roster to CBH via ProviderConnect sFTP.' },
          { id: '1.13.5', text: 'CBH initiates screening / credentialing — claiming may begin for up to 120 days while CBH screens.' },
        ],
      },
      {
        id: '1.14',
        number: '1.14',
        title: 'Student Batch Registration File — Initial Submission',
        items: [
          { id: '1.14.1', text: 'Collect health insurance information for every student to whom CYBHI services will be billed (MCP / Medi-Cal FFS / Knox-Keene plan / CDI plan).' },
          { id: '1.14.2', text: 'Complete the Student Batch Registration File (Excel template from CBH).' },
          { id: '1.14.3', text: 'Submit at least 24 hours BEFORE the first claim submission for those students.', note: 'CRITICAL — claims will reject if the student is not in the batch registration first.' },
          { id: '1.14.4', text: 'Establish a recurring upload cadence.' },
        ],
      },
      {
        id: '1.15',
        number: '1.15',
        title: 'Banking and Payment Setup',
        appliesTo: { pathways: ['A', 'B'], subPathwaysC: ['C2'] },
        items: [
          { id: '1.15.1', text: 'Designate the LEA bank account to receive CBH reimbursements; coordinate with CFO/business office.' },
          { id: '1.15.2', text: 'Note: initial payments arrive as paper checks; Payspan EFT is optional (see Optional Checklist §5.1).' },
        ],
      },
      {
        id: '1.16',
        number: '1.16',
        title: 'Required HIPAA / Security Foundations',
        items: [
          { id: '1.16.1', text: 'Appoint Security Officer per 45 CFR §164.308(a)(2).' },
          { id: '1.16.2', text: 'Conduct initial HIPAA risk analysis per §164.308(a)(1)(ii)(A).' },
          { id: '1.16.3', text: 'Document risk treatment plan per §164.308(a)(1)(ii)(B).' },
          { id: '1.16.4', text: 'Implement workforce security policies per §164.308(a)(3).' },
          { id: '1.16.5', text: 'Implement access management per §164.308(a)(4) — unique user IDs, role-based access.' },
          { id: '1.16.6', text: 'Implement audit controls per §164.312(b) — centralized log aggregation.' },
          { id: '1.16.7', text: 'Implement person/entity authentication per §164.312(d).' },
          { id: '1.16.8', text: 'Implement transmission security per §164.312(e) — TLS 1.2+ in transit.' },
          { id: '1.16.9', text: 'Document policies and procedures per §164.316 with 6-year retention minimum.' },
          { id: '1.16.10', text: 'Execute BAAs with every vendor that handles PHI.' },
        ],
      },
      {
        id: '1.17',
        number: '1.17',
        title: 'Required Documentation Infrastructure',
        items: [
          { id: '1.17.1', text: 'Stand up a clinical documentation system (EHR or equivalent) capable of capturing all CYBHI-required data elements: DOS, schoolsite/location, duration (start/stop), modality (in-person / sync AV / sync audio-only), service category, recipient (student or caregiver), participants for group, diagnosis (ICD-10) with allowance for F99/Z55-Z65/Z03.89, practitioner identity, supervisor identity for non-licensed practitioners.' },
          { id: '1.17.2', text: 'Stand up a billing system capable of generating HIPAA-compliant 837P claims with CYBHI-specific requirements.' },
          { id: '1.17.3', text: 'Configure the billing system rules: U4 modifier on every CYBHI claim line; U1 for dyadic; HA/HQ for psychoeducation individual/group; U2 for CHWs; 95/93 for telehealth; ORP NPI on Medi-Cal claims; mutual exclusion of U4 vs TL/TM.', note: 'U4 modifier missing = claim is not CYBHI. U4 + TL/TM on the same line = automatic rejection.' },
          { id: '1.17.4', text: 'Configure the 365-day timely filing window (DOS on or after 1/1/2026 per AB 144 / WIC §5961.4(d)(2)(C)(iii)(II); 180-day window for prior DOS).' },
        ],
      },
      {
        id: '1.18',
        number: '1.18',
        title: 'Required Practitioner & Staff Training',
        items: [
          { id: '1.18.1', text: 'Initial CYBHI-specific onboarding curriculum (2–4 hours) for every practitioner covering: program purpose, eligible services, schoolsite definition, documentation requirements, consent processes, telehealth requirements.' },
          { id: '1.18.2', text: 'ACEs Aware Core Training certification for any practitioner who will bill G9919 or G9920 (ACEs screening) — practitioner self-attests completion.' },
          { id: '1.18.3', text: 'EHR / documentation system training for every practitioner.' },
          { id: '1.18.4', text: 'Train billing staff on CYBHI claim assembly and submission.' },
          { id: '1.18.5', text: 'Train front-office staff on student batch registration data collection.' },
          { id: '1.18.6', text: 'Document training completion per practitioner — required under HIPAA §164.308(a)(5).' },
        ],
      },
      {
        id: '1.19',
        number: '1.19',
        title: 'Required Records Retention',
        items: [
          { id: '1.19.1', text: 'Implement 10-year records retention infrastructure for CYBHI records per 42 CFR §438.3(h).' },
          { id: '1.19.2', text: 'Confirm encounter documentation, claims, ERAs, denial records, appeal records, sanction records, training records all retained.' },
          { id: '1.19.3', text: 'Confirm records are searchable / retrievable for audit by DHCS, CBH, or federal regulators.' },
        ],
      },
      {
        id: '1.20',
        number: '1.20',
        title: 'Required Test Claims Before Go-Live',
        appliesTo: { pathways: ['A', 'B'], subPathwaysC: ['C2'] },
        items: [
          { id: '1.20.1', text: 'Submit a small batch of test claims to validate end-to-end flow.' },
          { id: '1.20.2', text: 'Confirm receipt of 999 (functional ack), 277CA (claim status), and 835 (ERA) responses.' },
          { id: '1.20.3', text: 'Resolve any rejection patterns before scaling to full production.' },
          { id: '1.20.4', text: 'Confirm first payment receipt.' },
        ],
      },
    ],
  },

  // ----------------------------------------------------------
  // §2 — Scenario-Specific Onboarding (Consortium Lead / Member)
  // ----------------------------------------------------------
  {
    id: 'on-2',
    number: '2',
    title: 'Scenario-Specific Onboarding',
    description:
      'Complete items in this section ONLY if the scenario applies to your LEA. Items in §1 apply to all scenarios.',
    category: 'onboarding',
    appliesTo: { pathways: ['B', 'C'] },
    sections: [
      {
        id: '2.1',
        number: '2.1',
        title: 'Consortium Lead — Additional Required Items',
        appliesTo: { pathways: ['B'] },
        items: [
          { id: '2.1.1', text: 'Collect attestation letters from each participating member.' },
          { id: '2.1.2', text: 'Submit attestation letters to DHCS as part of cohort enrollment.' },
          { id: '2.1.3', text: "Negotiate and execute MOU with each participating member covering: governance, division of responsibilities (data, claims, reimbursement), funds distribution schedule (CBH recommends ≤30 days from Lead's receipt), dispute resolution, termination." },
          { id: '2.1.4', text: 'Determine which of the three program functions the Lead will handle for each Member: data submission, claims submission, reimbursement receipt.' },
          { id: '2.1.5', text: 'Stand up consortium-wide SPI roster aggregation process.' },
          { id: '2.1.6', text: 'Stand up consortium-wide student batch registration aggregation process.' },
          { id: '2.1.7', text: 'Stand up consortium-wide claims submission pipeline (if Lead-submitted model).' },
          { id: '2.1.8', text: 'Stand up consortium-wide reimbursement distribution process — funds to Members within agreed timeframe.' },
          { id: '2.1.9', text: 'Stand up reporting back to each Member: claims submitted, paid, denied, reimbursement allocated.' },
          { id: '2.1.10', text: 'Implement cross-Member duplicate prevention — same student + DOS + procedure cannot be billed twice.' },
          { id: '2.1.11', text: "Decide and document whether the Lead's NPI or each Member's NPI appears as Billing Provider on claims." },
        ],
      },
      {
        id: '2.2',
        number: '2.2',
        title: 'Consortium Participating Member — Additional Required Items',
        appliesTo: { pathways: ['C'] },
        items: [
          { id: '2.2.1', text: 'Sign attestation letter committing to join the consortium under the Lead.' },
          { id: '2.2.2', text: 'Execute the appropriate PPA variant — Standard PPA with Medi-Cal enrollment if billing/reimbursing directly; Modified PPA without Medi-Cal enrollment if delegating both to Lead.' },
          { id: '2.2.3', text: "Provide the Lead with the data needed for the consortium's SPI roster (your practitioners' Type I NPIs, licenses, credentials)." },
          { id: '2.2.4', text: 'Provide the Lead with student insurance information for student batch registration.' },
          { id: '2.2.5', text: 'Negotiate and sign the MOU with the Lead.' },
          { id: '2.2.6', text: 'If receiving funds from Lead via MOU: confirm banking account, distribution schedule, and accounting treatment.' },
          { id: '2.2.7', text: 'If submitting data files directly to CBH (Sub-Pathway C3): set up own sFTP/ProviderConnect and sign tri-party DUA.', appliesTo: { subPathwaysC: ['C3'] } },
          { id: '2.2.8', text: 'If handling claims/reimbursement directly (Sub-Pathway C2): complete all items in §1.5, §1.7, §1.8, §1.12 (NPI, Medi-Cal, ORP, Availity).', appliesTo: { subPathwaysC: ['C2'] } },
        ],
      },
    ],
  },

  // ----------------------------------------------------------
  // §3 — Community-Provider Onboarding (S2 / S3 / S4)
  // ----------------------------------------------------------
  {
    id: 'on-3',
    number: '3',
    title: 'Community-Provider Onboarding',
    description:
      'Complete items in this section ONLY for the community-provider arrangements your LEA uses.',
    category: 'onboarding',
    appliesTo: { scenarios: ['S2', 'S3', 'S4'] },
    sections: [
      {
        id: '3.1',
        number: '3.1',
        title: 'Embedded Community Provider (LEA Contracts and Bills)',
        appliesTo: { scenarios: ['S2'] },
        items: [
          { id: '3.1.1', text: 'Execute a service contract between the LEA and the community provider (scope, rates, billing flow, insurance/liability, HIPAA/BAA, FERPA, termination, indemnification).' },
          { id: '3.1.2', text: 'Execute a HIPAA Business Associate Agreement (BAA) with the community provider.' },
          { id: '3.1.3', text: 'Collect Type I NPIs and credential information for each individual practitioner at the community provider.' },
          { id: '3.1.4', text: "Add the community provider to the LEA's SPI roster as Type G (Group)." },
          { id: '3.1.5', text: 'Add each individual practitioner to the SPI roster as Type I (licensed/PPS) or Type P (non-licensed/associate).' },
          { id: '3.1.6', text: 'LEA submits the SPI roster to CBH.' },
          { id: '3.1.7', text: 'LEA submits student batch registration for any students receiving services from embedded providers.' },
          { id: '3.1.8', text: "LEA submits claims with its own Type II NPI as Billing Provider; practitioner's Type I NPI as Rendering Provider; ORP NPI on Medi-Cal claims." },
          { id: '3.1.9', text: 'LEA receives CBH reimbursements and pays the community provider per contract.' },
        ],
      },
      {
        id: '3.2',
        number: '3.2',
        title: "Affiliated Community Provider via LEA's SPI Roster (Pathway #1)",
        description:
          'No contract or financial relationship between LEA and provider. Provider bills CBH directly.',
        appliesTo: { scenarios: ['S3'] },
        items: [
          { id: '3.2.1', text: 'Identify the affiliated provider; confirm willingness to participate.' },
          { id: '3.2.2', text: 'Verify the affiliated provider is Medi-Cal enrolled (mandatory) via DHCS Provider Master File. If not: provider enrolls through PAVE before designation.' },
          { id: '3.2.3', text: 'Notify CBH of intended designation at CYBHITPA@carelon.com.' },
          { id: '3.2.4', text: 'LEA adds the affiliated provider to its SPI roster as Type G.' },
          { id: '3.2.5', text: 'Confirm provider executes its OWN PPA with DHCS.' },
          { id: '3.2.6', text: 'Confirm provider executes its OWN DUA with CBH.' },
          { id: '3.2.7', text: 'LEA executes the DUA Affiliated Provider Addendum with CBH.' },
          { id: '3.2.8', text: 'Confirm provider establishes its OWN ProviderConnect / sFTP connection with CBH.' },
          { id: '3.2.9', text: 'Confirm provider establishes its OWN Availity account.' },
          { id: '3.2.10', text: 'Confirm provider completes its OWN SPI roster listing each individual practitioner.' },
          { id: '3.2.11', text: 'Confirm provider submits student batch registration files for the students they serve, identifying the associated LEA.' },
          { id: '3.2.12', text: 'Confirm provider submits claims directly to CBH; receives reimbursements directly.' },
        ],
      },
      {
        id: '3.3',
        number: '3.3',
        title: 'Statewide Affiliated Provider (Pathway #2)',
        description:
          'Multi-county, multi-site provider serving 10+ LEAs across 2+ counties via multiple sites or telehealth. Participates without LEA being in an active cohort.',
        appliesTo: { scenarios: ['S4'] },
        items: [
          { id: '3.3.1', text: 'Identify the statewide affiliated provider.' },
          { id: '3.3.2', text: 'Confirm the provider has been deemed eligible by DHCS (provider initiates this via DHCS.SBS@dhcs.ca.gov).' },
          { id: '3.3.3', text: 'Complete the CYBHI Fee Schedule Program — Affiliated Provider Designation and Acknowledgement Letter (template in Appendix B of the Community Provider Guidance).' },
          { id: '3.3.4', text: 'Letter on LEA letterhead, signed by Authorized Representative.' },
          { id: '3.3.5', text: 'Submit the signed letter to DHCS.SBS@dhcs.ca.gov.' },
          { id: '3.3.6', text: 'In this pathway, the LEA does NOT need to add the provider to its SPI roster.' },
          { id: '3.3.7', text: 'The LEA does NOT need to be in an active cohort.' },
        ],
      },
    ],
  },

  // ----------------------------------------------------------
  // §4 — Vendor Onboarding (S5 / S6)
  // ----------------------------------------------------------
  {
    id: 'on-4',
    number: '4',
    title: 'Vendor Onboarding',
    description:
      'Required items by vendor type. Complete only for the vendor arrangements your LEA uses.',
    category: 'onboarding',
    appliesTo: { scenarios: ['S5', 'S6'] },
    sections: [
      {
        id: '4.1',
        number: '4.1',
        title: 'Third-Party Billing Vendor — Required Items',
        appliesTo: { scenarios: ['S5'] },
        items: [
          { id: '4.1.1', text: "Confirm vendor's experience with CYBHI specifically (current LEA client references)." },
          { id: '4.1.2', text: "Confirm vendor's experience with Availity Essentials and CBH ProviderConnect." },
          { id: '4.1.3', text: "Confirm vendor's capacity for: SPI roster maintenance, student batch registration submission, U4 modifier handling, ORP NPI inclusion, 365-day filing window." },
          { id: '4.1.4', text: "Verify vendor's HIPAA/FERPA compliance: SOC 2 Type II report; US-based data hosting; encryption at rest and in transit." },
          { id: '4.1.5', text: 'Confirm vendor is NOT excluded from federal programs (OIG / SAM exclusion check).' },
          { id: '4.1.6', text: 'Execute Service Agreement covering scope, SLAs, pricing, term, termination, indemnification, data ownership, audit rights.' },
          { id: '4.1.7', text: 'Execute HIPAA BAA with vendor.' },
          { id: '4.1.8', text: 'Execute TRI-PARTY DUA between LEA, vendor, and CBH.', note: 'Mandatory — vendor cannot handle CYBHI data without this.' },
          { id: '4.1.9', text: 'Define data-exchange protocol with vendor: sFTP location, file formats, schedule, encryption.' },
          { id: '4.1.10', text: "Define EHR-to-vendor data flow (API pull, LEA export upload, or vendor inside LEA's EHR)." },
        ],
      },
      {
        id: '4.2',
        number: '4.2',
        title: 'EHR / Documentation Vendor — Required Items',
        appliesTo: { scenarios: ['S6'] },
        items: [
          { id: '4.2.1', text: 'Confirm EHR captures every CYBHI-required documentation element (see §1.17).' },
          { id: '4.2.2', text: 'Confirm EHR supports IEP/IFSP linkage capability (to distinguish CYBHI vs. LEA-BOP-routed services).' },
          { id: '4.2.3', text: 'Confirm consent management capability (electronic consent, minor consent flags per CMIA / CA Civil Code §56.10 / §56.107).' },
          { id: '4.2.4', text: 'Confirm reporting/export capability to the billing vendor or in-house billing system.' },
          { id: '4.2.5', text: 'Confirm HIPAA audit log of every document creation, modification, view, deletion.' },
          { id: '4.2.6', text: 'Confirm role-based access controls.' },
          { id: '4.2.7', text: "Verify vendor's HIPAA compliance: SOC 2 Type II; US hosting; encryption at rest (AES-256, FIPS 140-3) and in transit (TLS 1.2+)." },
          { id: '4.2.8', text: 'Confirm records retention compliant with 10-year CYBHI requirement.' },
          { id: '4.2.9', text: 'Execute Master Services Agreement with the vendor.' },
          { id: '4.2.10', text: 'Execute HIPAA BAA with vendor.' },
          { id: '4.2.11', text: 'Execute FERPA compliance addendum if vendor handles student education records.' },
          { id: '4.2.12', text: 'Confirm data return / destruction terms at contract end.' },
          { id: '4.2.13', text: 'If EHR vendor shares data with CBH directly: separate tri-party DUA with CBH required.' },
        ],
      },
    ],
  },

  // ==========================================================
  // OPTIONAL — PDF Part 2 (Section 5)
  // ==========================================================
  {
    id: 'opt-5',
    number: '5',
    title: 'Recommended Best Practices',
    description:
      'Not required by DHCS or CBH, but materially improve security posture, denial rates, audit defensibility, and operational performance. Each item annotates the benefit it delivers.',
    category: 'optional',
    sections: [
      {
        id: '5.1',
        number: '5.1',
        title: 'Financial Operations Optimization',
        items: [
          { id: '5.1.1', text: 'Set up Payspan account with CBH for Electronic Funds Transfer (EFT). After the LEA receives the first paper check from CBH, register on Payspan to convert all subsequent payments to EFT.', note: 'Eliminates paper-check handling and lockbox delays; faster cash flow.' },
          { id: '5.1.2', text: 'Implement a cash-application reconciliation tool that matches CBH 835 ERAs to bank deposits daily.', note: 'Catches discrepancies within 24 hours instead of monthly close.' },
          { id: '5.1.3', text: 'Implement a contractual write-off policy with automated thresholds.', note: 'Reduces manual workload on small-balance adjustments.' },
        ],
      },
      {
        id: '5.2',
        number: '5.2',
        title: 'Enhanced Documentation & EHR Capabilities',
        items: [
          { id: '5.2.1', text: 'Adopt a behavioral-health-specific EHR (NextGen, Welligent, Qualifacts CareLogic, etc.) rather than relying solely on the SIS.', note: 'Built-in clinical templates, IEP/IFSP linkage, structured assessment instruments, better claim-ready documentation.' },
          { id: '5.2.2', text: 'Integrate the EHR with the Student Information System (Aeries, PowerSchool, Synergy) for student demographics and enrollment.', note: 'Eliminates dual data entry; ensures roster is always current.' },
          { id: '5.2.3', text: 'Implement HIPAA-compliant telehealth platform (Doxy, Zoom for Healthcare, etc.) tightly integrated with the EHR.', note: 'Documents telehealth modality automatically for accurate 93/95 modifier derivation.' },
          { id: '5.2.4', text: 'Implement mobile / tablet-friendly clinical documentation.', note: 'Supports mobile counseling, off-campus clinics, schoolsite-by-schoolsite work without round trips.' },
          { id: '5.2.5', text: 'Implement structured assessment instruments (PHQ-9, GAD-7, ACE Questionnaire) inside the EHR.', note: 'Auto-captures diagnostic codes; supports ACEs screening documentation for G9919/G9920.' },
        ],
      },
      {
        id: '5.3',
        number: '5.3',
        title: 'Backend Rules Engine for Modifier Derivation',
        items: [
          { id: '5.3.1', text: 'Implement a versioned, machine-readable rules engine in the billing system (Charge Router pattern).', note: 'Eliminates clinician modifier entry; reduces denial rate; auditable derivation chain.' },
          { id: '5.3.2', text: 'Implement effective-dated rule registry.', note: 'Correctly bills claims at the rule version in effect on the DOS, not on submission.' },
          { id: '5.3.3', text: 'Implement workqueue tooling for charge errors with SLAs and aging reports.', note: 'Prevents revenue leakage from forgotten error queues.' },
          { id: '5.3.4', text: 'Implement denial-feedback loop into rule library.', note: 'Continuously reduces denial rate by category.' },
        ],
      },
      {
        id: '5.4',
        number: '5.4',
        title: 'Enhanced Security (Beyond HIPAA Minimum)',
        items: [
          { id: '5.4.1', text: 'Implement Multi-Factor Authentication (MFA) for all access to ePHI systems (phishing-resistant where feasible).', note: 'Currently addressable in HIPAA Security Rule; PROPOSED REQUIRED in 2024 NPRM.' },
          { id: '5.4.2', text: 'Implement Endpoint Detection and Response (EDR) on all workstations and servers.', note: 'Detects ransomware and lateral movement; reduces dwell time.' },
          { id: '5.4.3', text: 'Implement SIEM with use cases tuned to ePHI access anomalies.', note: 'Faster breach detection; defensible discovery date.' },
          { id: '5.4.4', text: 'Implement network segmentation for ePHI systems.', note: 'Limits blast radius of compromise; PROPOSED REQUIRED in 2024 NPRM.' },
          { id: '5.4.5', text: 'Implement immutable / air-gapped backups with quarterly restoration tests.', note: 'Ransomware resilience; PROPOSED REQUIRED in NPRM.' },
          { id: '5.4.6', text: 'Implement vulnerability scanning (monthly internal, quarterly external) and annual penetration testing.', note: 'PROPOSED REQUIRED in NPRM; reduces breach risk.' },
          { id: '5.4.7', text: 'Pursue HITRUST or SOC 2 Type II + HIPAA attestation.', note: 'Independent third-party validation; reduces audit friction with payers.' },
          { id: '5.4.8', text: 'Implement Data Loss Prevention (DLP) for outbound ePHI.', note: 'Prevents accidental external disclosure.' },
          { id: '5.4.9', text: 'Implement Privileged Access Management (PAM) for administrative accounts.', note: 'Reduces insider risk.' },
        ],
      },
      {
        id: '5.5',
        number: '5.5',
        title: 'Consortium Optimization',
        appliesTo: { pathways: ['B', 'C'] },
        items: [
          { id: '5.5.1', text: 'Implement consortium-wide shared EHR with COE-managed infrastructure.', note: 'Lowers per-LEA cost; standardizes documentation.' },
          { id: '5.5.2', text: 'Implement consortium peer-to-peer onboarding discussions.', note: 'Member LEAs learn from Lead and earlier members.' },
          { id: '5.5.3', text: 'Implement shared third-party billing vendor across consortium.', note: 'Volume pricing; consistent claim quality.' },
        ],
      },
      {
        id: '5.6',
        number: '5.6',
        title: 'Provider Network Optimization',
        items: [
          { id: '5.6.1', text: "Build a designated 'super-user' clinician network for EHR and CYBHI documentation.", note: 'Distributed support; reduced training burden.' },
          { id: '5.6.2', text: 'Implement pre-go-live shadow sessions for new clinicians.', note: 'Better documentation quality from day one.' },
          { id: '5.6.3', text: 'Maintain affiliated/embedded provider relationships proactively.', note: 'Expanded service capacity without LEA staffing.', appliesTo: { scenarios: ['S2', 'S3', 'S4'] } },
          { id: '5.6.4', text: 'Calendar an annual review of every designated affiliated provider.', note: 'Catches inactive providers; reduces audit findings.', appliesTo: { scenarios: ['S3', 'S4'] } },
        ],
      },
      {
        id: '5.7',
        number: '5.7',
        title: 'Reporting and Analytics',
        items: [
          { id: '5.7.1', text: 'Implement a monthly executive dashboard: encounters documented, claims submitted, claims paid, denial rate, days in AR, top denial reasons.', note: 'Leadership visibility into program performance.' },
          { id: '5.7.2', text: 'Implement per-practitioner productivity tracking.', note: 'Identifies training needs and capacity allocation.' },
          { id: '5.7.3', text: 'Implement per-payer performance tracking (MCP, FFS, Knox-Keene, CDI).', note: 'Identifies payer-specific issues.' },
          { id: '5.7.4', text: 'Implement annual outcome reporting.', note: 'Supports board reporting and grant applications; demonstrates program value.' },
          { id: '5.7.5', text: 'Implement benchmarking against other CYBHI participants where data is available.', note: 'Identifies efficiency gaps.' },
        ],
      },
      {
        id: '5.8',
        number: '5.8',
        title: 'Compliance & Audit Defensibility',
        items: [
          { id: '5.8.1', text: 'Build a central evidence vault with artifacts tagged to OCR HIPAA Audit Protocol items.', note: 'Audit response time drops from weeks to minutes.' },
          { id: '5.8.2', text: 'Conduct an annual internal audit against all 180 OCR Audit Protocol items.', note: 'Identifies gaps before regulators do.' },
          { id: '5.8.3', text: 'Conduct tabletop exercises for breach response at least annually.', note: 'Sharpens 60-day notification workflow.' },
          { id: '5.8.4', text: 'Document the four-factor breach risk assessment template before any incident occurs.', note: 'Faster, defensible assessment when needed.' },
          { id: '5.8.5', text: 'Pre-position substitute-notice infrastructure (website mechanism, mailing vendor, toll-free vendor).' },
          { id: '5.8.6', text: 'Implement tighter BAA notification clauses (24–48 hours from BA discovery, rather than 60-day default).', note: "Preserves CE's ability to meet the 60-day clock." },
        ],
      },
      {
        id: '5.9',
        number: '5.9',
        title: 'Forward Compatibility with the 2024 HIPAA NPRM',
        description:
          'The December 2024 Security Rule NPRM is proposed (not yet final). When finalized, regulated entities will have 240 days to comply. Building forward-compatible from the start avoids a costly second project.',
        items: [
          { id: '5.9.1', text: 'Implement universal encryption at rest and in transit (eliminating addressable exception use).' },
          { id: '5.9.2', text: 'Implement universal MFA on ePHI access.' },
          { id: '5.9.3', text: 'Implement asset inventory with annual refresh.' },
          { id: '5.9.4', text: 'Implement network map showing ePHI flow with annual refresh.' },
          { id: '5.9.5', text: 'Implement annual BA technical-safeguard verification with written certification.' },
          { id: '5.9.6', text: 'Implement 24-hour workforce-access-change notification (HRIS-to-IAM automation).' },
          { id: '5.9.7', text: 'Implement 72-hour critical-system restoration capability.' },
          { id: '5.9.8', text: 'Implement annual compliance audit covering every Security Rule standard.' },
        ],
      },
      {
        id: '5.10',
        number: '5.10',
        title: 'Parent and Caregiver Engagement',
        items: [
          { id: '5.10.1', text: 'Distribute the CYBHI Parent and Caregiver Flyer / Brochure at parent-teacher conferences and back-to-school nights.', note: 'Increases family awareness and referrals.' },
          { id: '5.10.2', text: 'Use the DHCS-provided sample parent-teacher conference script and back-to-school night script.', note: 'Consistent messaging.' },
          { id: '5.10.3', text: "Translate parent materials into the LEA's threshold languages (DHCS provides templates in Spanish; contact DHCS.SBS@dhcs.ca.gov for additional languages)." },
          { id: '5.10.4', text: 'Train front-office staff on CYBHI parent communications.' },
        ],
      },
      {
        id: '5.11',
        number: '5.11',
        title: 'Workforce Optimization',
        items: [
          { id: '5.11.1', text: 'Develop a CYBHI-funded staffing plan for the LEA — additional school social workers, counselors, MFTs, LPCCs, psychologists, CHWs, CWCs.', note: 'Expanded direct-service capacity supported by CYBHI revenue.' },
          { id: '5.11.2', text: 'Implement supervision infrastructure for non-licensed/associate practitioners (AMFT, ACSW, APCC, CHW, CWC).', note: 'Expands the eligible practitioner pool; meets supervision requirements.' },
          { id: '5.11.3', text: 'Implement clinician burnout monitoring.', note: "Sustains workforce; CYBHI's value depends on practitioner retention." },
        ],
      },
    ],
  },

  // ==========================================================
  // OPERATIONAL — PDF Part 3 (Section 6)
  // ==========================================================
  {
    id: 'op-6',
    number: '6',
    title: 'Ongoing Operations',
    description:
      'Recurring activities the LEA must perform after go-live to maintain compliance and operational performance. Grouped by frequency.',
    category: 'operational',
    sections: [
      {
        id: '6.1',
        number: '6.1',
        title: 'Daily / Weekly Operations',
        items: [
          { id: '6.1.1', text: 'Clinicians document services in the EHR within the timeframe specified by LEA policy (typically within 24–72 hours of service).' },
          { id: '6.1.2', text: 'Supervisors co-sign documentation for non-licensed/associate practitioners within required timeframe.' },
          { id: '6.1.3', text: 'Front-office staff updates student insurance information for new students or coverage changes.' },
          { id: '6.1.4', text: 'Submit student batch registration file updates as new students are served.', note: 'CRITICAL: always at least 24 hours BEFORE first claim for those students.' },
          { id: '6.1.5', text: 'Update student batch registration when student coverage changes.' },
          { id: '6.1.6', text: "Generate claims per the LEA's claiming cadence (typically weekly; some LEAs daily)." },
          { id: '6.1.7', text: 'Submit claims via Availity Essentials.', appliesTo: { pathways: ['A', 'B'], subPathwaysC: ['C2'] } },
          { id: '6.1.8', text: 'Monitor Availity for 999 (functional acknowledgment) and 277CA (claim status) responses.', appliesTo: { pathways: ['A', 'B'], subPathwaysC: ['C2'] } },
          { id: '6.1.9', text: 'Address any 999/277CA rejections promptly — these never produce an 835.', appliesTo: { pathways: ['A', 'B'], subPathwaysC: ['C2'] } },
          { id: '6.1.10', text: 'Process 835 ERAs as they arrive; post payments to claims and lines.', appliesTo: { pathways: ['A', 'B'], subPathwaysC: ['C2'] } },
          { id: '6.1.11', text: 'Identify denials and queue for follow-up; track unallocated cash and resolve within 30-day SLA.', appliesTo: { pathways: ['A', 'B'], subPathwaysC: ['C2'] } },
          { id: '6.1.12', text: 'Categorize denials by CARC code; route to appropriate work queue based on denial reason.' },
          { id: '6.1.13', text: 'Work denials within SLA (typically 14 days for resubmission-eligible).' },
        ],
      },
      {
        id: '6.2',
        number: '6.2',
        title: 'Monthly Operations',
        items: [
          { id: '6.2.1', text: 'Submit SPI roster updates as practitioners join, leave, or change credentials.', note: 'Required.' },
          { id: '6.2.2', text: 'Remove inactive practitioners from the roster.' },
          { id: '6.2.3', text: 'Add new practitioners (only after Type I NPI verification and credential verification).' },
          { id: '6.2.4', text: 'Update supervisor pairings for non-licensed practitioners.' },
          { id: '6.2.5', text: 'Reconcile CBH 835 ERAs to bank deposits.' },
          { id: '6.2.6', text: 'Reconcile to LEA general ledger.' },
          { id: '6.2.7', text: 'If consortium Lead: distribute funds to Members per MOU within agreed timeframe (≤30 days recommended).', appliesTo: { pathways: ['B'] } },
          { id: '6.2.8', text: 'Run AR aging report; identify aged claims approaching the 365-day filing deadline.' },
          { id: '6.2.9', text: 'Review denial trends by CARC code, payer, practitioner, procedure.' },
          { id: '6.2.10', text: 'Identify root causes; brief clinical and billing staff on patterns; update rules engine if a derivable cause is identified.' },
          { id: '6.2.11', text: 'Cross-program duplicate check (if dual-enrolled in LEA-BOP): same student + same DOS + same procedure code cannot be billed to BOTH CYBHI and LEA-BOP. Investigate and correct any duplicates found.' },
          { id: '6.2.12', text: 'Process new-practitioner onboarding (NPI, credential verification, EHR training, SPI roster update).' },
          { id: '6.2.13', text: 'Process practitioner departures (SPI roster update, account deactivation, accountability for in-flight documentation).' },
        ],
      },
      {
        id: '6.3',
        number: '6.3',
        title: 'Quarterly Operations',
        items: [
          { id: '6.3.1', text: 'Access review for all users of ePHI systems per HIPAA §164.308(a)(4) — quarterly attestation by managers.', note: 'Required.' },
          { id: '6.3.2', text: 'Remove access for any user who no longer needs it.' },
          { id: '6.3.3', text: 'Document the review (auditor will ask).' },
          { id: '6.3.4', text: 'Run vulnerability scans (monthly internal, quarterly external is industry standard).' },
          { id: '6.3.5', text: 'Review SIEM alerts and incident response metrics.' },
          { id: '6.3.6', text: 'Patch management review.' },
          { id: '6.3.7', text: 'Review affiliated / embedded provider designations; confirm community providers are still active and willing to continue; terminate any inactive designations in writing to BOTH DHCS and CBH.', appliesTo: { scenarios: ['S2', 'S3', 'S4'] } },
          { id: '6.3.8', text: 'Review billing vendor performance against SLAs (denial rate, days in AR, response time).', appliesTo: { scenarios: ['S5'] } },
          { id: '6.3.9', text: 'Review EHR vendor performance against SLAs (uptime, support response, feature delivery).', appliesTo: { scenarios: ['S6'] } },
          { id: '6.3.10', text: 'Refresh practitioner training as needed for DHCS policy updates; refresh billing staff training on rule changes.' },
        ],
      },
      {
        id: '6.4',
        number: '6.4',
        title: 'Annual Operations',
        items: [
          { id: '6.4.1', text: 'HIPAA risk analysis refresh per §164.308(a)(1)(ii)(A).', note: 'Required.' },
          { id: '6.4.2', text: 'HIPAA security evaluation per §164.308(a)(8).' },
          { id: '6.4.3', text: 'Workforce security and awareness training refresh per §164.308(a)(5).' },
          { id: '6.4.4', text: 'Disaster recovery / contingency plan test per §164.308(a)(7)(ii)(D).' },
          { id: '6.4.5', text: 'Documentation review and update per §164.316(b)(2)(iii).' },
          { id: '6.4.6', text: 'Review and update PPA / DUA documents if DHCS / CBH issues new versions.' },
          { id: '6.4.7', text: 'Refresh standing ORP recommendations for each student where applicable (must be reviewed at least annually based on evidence-based guidelines).', appliesTo: { pathways: ['A', 'B'], subPathwaysC: ['C2'] } },
          { id: '6.4.8', text: 'Review the SPI roster comprehensively; remove inactive practitioners; re-verify credentials.' },
          { id: '6.4.9', text: 'Renew or refresh designation letters for affiliated providers if required by LEA policy.', appliesTo: { scenarios: ['S3', 'S4'] } },
          { id: '6.4.10', text: 'Review the CYBHI revenue, denial rate, AR aging against benchmarks; report CYBHI program performance to governance (board, superintendent, CFO).' },
          { id: '6.4.11', text: "Re-verify every practitioner's license/credential status (no lapses).", note: 'Required.' },
          { id: '6.4.12', text: 'Re-verify ACEs Aware Core Training currency for any practitioner billing G9919/G9920.' },
          { id: '6.4.13', text: 'Confirm supervisor relationships for non-licensed practitioners are still active.' },
          { id: '6.4.14', text: 'Request updated SOC 2 Type II report from billing vendor and EHR vendor; request subprocessor list updates; review BAAs for any required updates.', appliesTo: { scenarios: ['S5', 'S6'] } },
        ],
      },
      {
        id: '6.5',
        number: '6.5',
        title: 'Every 5 Years Operations',
        items: [
          { id: '6.5.1', text: 'Complete Medi-Cal provider revalidation via PAVE.', note: 'LAPSE CAN SUSPEND PAYMENT — calendar this from the day initial enrollment is approved.', appliesTo: { pathways: ['A', 'B'], subPathwaysC: ['C2'] } },
          { id: '6.5.2', text: 'Major vendor contract renewals (typically multi-year, requiring competitive review).', appliesTo: { scenarios: ['S5', 'S6'] } },
          { id: '6.5.3', text: 'Re-bid billing vendor or EHR vendor if contract terms permit.', appliesTo: { scenarios: ['S5', 'S6'] } },
        ],
      },
      {
        id: '6.6',
        number: '6.6',
        title: 'Event-Driven Operations',
        items: [
          { id: '6.6.1', text: 'NEW PRACTITIONER — Verify Type I NPI; verify credential / license; pair with supervisor if non-licensed; add to EHR with appropriate role-based access; complete initial CYBHI training (2–4 hours); complete EHR training; add to SPI roster and submit update to CBH; verify ACEs training if practitioner will bill G9919/G9920.' },
          { id: '6.6.2', text: 'PRACTITIONER DEPARTURE — Remove from SPI roster and submit update to CBH; deactivate EHR and billing system accounts (same day per HIPAA termination procedure); reassign any in-flight documentation or open patient records; document offboarding.' },
          { id: '6.6.3', text: 'NEW SCHOOLSITE OR SERVICE LOCATION — Add to SPI roster; confirm schoolsite definition compliance per HSC §1374.722(b)(6); update encounter location options in EHR; train any new on-site staff.' },
          { id: '6.6.4', text: 'SECURITY INCIDENT — Investigate per the incident response plan; contain the incident; conduct the four-factor breach risk assessment per §164.402.' },
          { id: '6.6.5', text: 'SECURITY INCIDENT — Notify CBH per the DUA (typically 24–48 hours from discovery).' },
          { id: '6.6.6', text: 'SECURITY INCIDENT — If breach confirmed and 500+ individuals affected: notify HHS OCR within 60 days.' },
          { id: '6.6.7', text: 'SECURITY INCIDENT — If breach confirmed: notify affected individuals within 60 days from discovery.' },
          { id: '6.6.8', text: 'SECURITY INCIDENT — Notify state AG if required by California state law (sometimes shorter than 60 days).' },
          { id: '6.6.9', text: 'SECURITY INCIDENT — Post-incident review and remediation.' },
          { id: '6.6.10', text: 'DHCS / CBH POLICY UPDATE — Review the policy change for impact on workflows; update rules in billing system if needed (effective-dated); update SOPs; retrain staff if needed; communicate impact to consortium members if applicable.' },
          { id: '6.6.11', text: 'DENIAL PATTERN CHANGE — Conduct root cause analysis; adjust rules engine if a derivable cause is identified; retrain staff if needed; escalate to CBH if payer behavior is non-compliant with CYBHI rules.' },
          { id: '6.6.12', text: 'LEA GOVERNANCE CHANGE — Re-verify Authorized Representative; update DHCS and CBH on changes; update PPA / DUA signatory if needed; update banking authority for CBH payments if needed.' },
          { id: '6.6.13', text: 'CONSORTIUM MEMBER JOINS OR LEAVES — Obtain attestation letter (joining) or termination notice (leaving); notify DHCS at DHCS.SBS@dhcs.ca.gov; update MOU set; update SPI roster aggregation; update student batch registration aggregation; update funds distribution model.', appliesTo: { pathways: ['B'] } },
          { id: '6.6.14', text: 'AFFILIATED PROVIDER TERMINATION — Written notice to DHCS at DHCS.SBS@dhcs.ca.gov AND to CBH at CYBHITPA@carelon.com; remove from SPI roster; document the termination.', appliesTo: { scenarios: ['S3', 'S4'] } },
          { id: '6.6.15', text: 'VENDOR CHANGE — Negotiate transition plan; ensure data export from outgoing vendor (LEA owns the data); confirm data destruction by outgoing vendor; sign new agreements with incoming vendor (Service Agreement, BAA, tri-party DUA with CBH); update CBH on the vendor change; test end-to-end flow with new vendor before cutover.', appliesTo: { scenarios: ['S5', 'S6'] } },
        ],
      },
      {
        id: '6.7',
        number: '6.7',
        title: 'Continuous Improvement',
        items: [
          { id: '6.7.1', text: 'Closed-loop denial-to-rule feedback (move recurring denial categories into the rules engine).' },
          { id: '6.7.2', text: 'Annual rule registry review against DHCS APLs, CYBHI Manual revisions, SPAs, and CMS guidance.' },
          { id: '6.7.3', text: 'Onboarding new programs (e.g., future SMAA, PPS-credential changes, new CYBHI service codes) — reuse the general engine.' },
          { id: '6.7.4', text: 'Quarterly review of operational metrics: clean claim rate, days in AR, denial rate by category, top denial reasons, productivity per practitioner.' },
        ],
      },
    ],
  },
];
